import { Component, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import {
  AeonProductsGQL,
  DeleteAeonProductGQL,
  RegisterQueueAeonGQL,
  UpdateUserAeonProductGQL,
} from 'src/gql/generated';
import { AeonProduct, AeonStock } from 'src/app/models/aeon-product';
import { MessageService } from 'src/app/services/message.service';
import { UserService } from 'src/app/services/user.service';
import { LoggingGQL } from '../../../gql/generated';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PanelModule } from 'primeng/panel';
import { Textarea } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { StoreStockComponent } from '../parts/store-stock/store-stock.component';
import { KeepaGraphComponent } from '../parts/keepa-graph/keepa-graph.component';
import { StorePresetDialogComponent } from '../store-preset-dialog/store-preset-dialog.component';
import { StorePreset } from '../../services/store-preset.service';
import { isValidJan } from 'src/app/utils/jan-validator';
import moment from 'moment';

@Component({
  selector: 'app-aeon',
  templateUrl: './aeon.component.html',
  styleUrls: ['./aeon.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    PanelModule,
    Textarea,
    ButtonModule,
    TooltipModule,
    CheckboxModule,
    MultiSelectModule,
    TableModule,
    InputTextModule,
    DialogModule,
    StoreStockComponent,
    KeepaGraphComponent,
    StorePresetDialogComponent,
  ],
})
export class AeonComponent {
  searchText: string;
  isLoading: boolean;

  products: Array<AeonProduct> = [];
  showZeroStock = false;
  showAnotherPriceModal = false;
  anotherPriceModel = {
    aeonProduct: null,
    price: null,
  };

  canRegisterNewItems = true;

  selectedArea = [];
  groupedArea = [];
  selectedPref = [];
  groupedPref = [];
  selectedStores = [];
  groupedStores = [];

  // 全店舗データを保持（フィルタ連動用）
  allPrefStores: string[] = [];

  queueItems = [];

  splitStr = '::';
  selectedAreaKey = 'savedAeonSelectedAreaKey';
  selectedPrefKey = 'savedAeonSelectedPrefKey';
  selectedStoreKey = 'savedAeonSelectedStoreKey';

  // 都道府県から地域へのマッピング
  private prefectureToArea: { [key: string]: string } = {
    北海道: '北海道・東北',
    青森県: '北海道・東北',
    岩手県: '北海道・東北',
    宮城県: '北海道・東北',
    秋田県: '北海道・東北',
    山形県: '北海道・東北',
    福島県: '北海道・東北',
    茨城県: '関東',
    栃木県: '関東',
    群馬県: '関東',
    埼玉県: '関東',
    千葉県: '関東',
    東京都: '関東',
    神奈川県: '関東',
    新潟県: '中部・北陸',
    富山県: '中部・北陸',
    石川県: '中部・北陸',
    福井県: '中部・北陸',
    山梨県: '中部・北陸',
    長野県: '中部・北陸',
    岐阜県: '中部・北陸',
    静岡県: '中部・北陸',
    愛知県: '中部・北陸',
    三重県: '近畿',
    滋賀県: '近畿',
    京都府: '近畿',
    大阪府: '近畿',
    兵庫県: '近畿',
    奈良県: '近畿',
    和歌山県: '近畿',
    鳥取県: '中国・四国',
    島根県: '中国・四国',
    岡山県: '中国・四国',
    広島県: '中国・四国',
    山口県: '中国・四国',
    徳島県: '中国・四国',
    香川県: '中国・四国',
    愛媛県: '中国・四国',
    高知県: '中国・四国',
    福岡県: '九州・沖縄',
    佐賀県: '九州・沖縄',
    長崎県: '九州・沖縄',
    熊本県: '九州・沖縄',
    大分県: '九州・沖縄',
    宮崎県: '九州・沖縄',
    鹿児島県: '九州・沖縄',
    沖縄県: '九州・沖縄',
  };

  constructor(
    private elementRef: ElementRef,
    private message: MessageService,
    private subtitleService: SubtitleService,
    private routingService: RoutingService,
    private userService: UserService,
    private createGql: RegisterQueueAeonGQL,
    private getGql: AeonProductsGQL,
    private userAeonUpdateGql: UpdateUserAeonProductGQL,
    private deleteAeonProductGQL: DeleteAeonProductGQL,
    private loggingGql: LoggingGQL,
  ) {
    this.isLoading = false;
    this.routingService.loginRequired();
    this.userService.aeonRequired();
    this.subtitleService.setSubTitle('イオン');
  }

  get filteredProducts(): Array<AeonProduct> {
    return this.products.filter((product) => {
      if (!product.hasAnyStock && !this.showZeroStock) {
        return false;
      }
      const hasFilteredStock = product.filteredStocks.filter((s) => s.hasStock).length > 0;
      if (!hasFilteredStock && !this.showZeroStock) {
        return false;
      }
      return true;
    });
  }

  get filteredProductsCount(): number {
    return this.filteredProducts.length;
  }

  async ngOnInit() {
    this.selectedArea = JSON.parse(localStorage.getItem(this.selectedAreaKey)) || [];
    this.selectedPref = JSON.parse(localStorage.getItem(this.selectedPrefKey)) || [];
    this.selectedStores = JSON.parse(localStorage.getItem(this.selectedStoreKey)) || [];

    // 初回は全件取得
    await this.loadProducts();
  }

  async loadProducts() {
    const resp = await firstValueFrom(
      this.getGql.fetch({
        variables: {
          prefectures: [],
        },
      }),
    );

    this.canRegisterNewItems = resp.data.assertAeonProducts.canRegisterNewItem;

    this.queueItems = resp.data.queueAeonProducts;
    this.products = [];
    const prefSet = new Set<string>();
    const prefStores: string[] = [];

    resp.data.aeonProducts.forEach((p) => {
      if (p.isDeleted) {
        // 削除済みは非表示
        return;
      }

      // Extract Amazon product information
      const sp = p.product;
      const mainAp = sp.amazonProducts?.find((ap) => ap.isMain);
      const ap = mainAp ?? (sp.amazonProducts?.length > 0 ? sp.amazonProducts[0] : null);

      const product: AeonProduct = {
        id: p.id,
        jan: sp.jan,
        price: p.price,
        name: ap?.name || p.productName,
        lastCheckedAt: p.lastCheckedAt,
        createdAt: p.createdAt,
        hasAnyStock: false,
        all: [],
        totalStockNum: p.totalStockNum,
        userAeonId: p.userAeonProduct?.id,
        userName: p.userAeonProduct?.user?.name,
        anotherPrice: p.userAeonProduct?.anotherPrice,
        // Amazon product information
        asin: ap?.asin || '',
        amazonPrice: ap?.latestPrice || -1,
        imageUrl: ap?.imageUrl || '',
        boughtPastMonth: ap?.keepaProduct?.boughtPastMonth,
      };

      p.stocks?.forEach((s) => {
        if (s.hasStock) {
          product.hasAnyStock = true;
        }
        prefSet.add(s.prefecture);
        const prefStore = s.prefecture + this.splitStr + s.storeName;
        if (!prefStores.includes(prefStore)) {
          prefStores.push(prefStore);
        }
        const stock: AeonStock = {
          prefecture: s.prefecture,
          storeName: s.storeName,
          hasStock: s.hasStock,
          stockValue: s.stockValue,
          note: s.note,
        };
        product.all.push(stock);
      });

      // 都道府県でソート
      product.all.sort((s1, s2) => (s1.prefecture > s2.prefecture ? 1 : -1));
      product.filteredStocks = this.doFilters(product.all);

      this.products.push(product);
    });

    this.allPrefStores = prefStores;
    this.buildArea();
    this.buildPrefOptions(prefSet);
    this.rebuildGroupedStores();
    this.logging();
  }

  buildArea() {
    this.groupedArea = [
      {
        label: 'エリア',
        value: 'area',
        items: [
          { label: '北海道・東北', value: '北海道・東北' },
          { label: '関東', value: '関東' },
          { label: '中部・北陸', value: '中部・北陸' },
          { label: '近畿', value: '近畿' },
          { label: '中国・四国', value: '中国・四国' },
          { label: '九州・沖縄', value: '九州・沖縄' },
        ],
      },
    ];
  }

  getAreaByPrefecture(prefecture: string): string {
    return this.prefectureToArea[prefecture] || '';
  }

  buildPrefOptions(prefSet: Set<string>) {
    // 地域ごとに都道府県をグルーピング
    const areaMap = new Map<string, string[]>();

    // 地域の表示順序を定義
    const areaOrder = ['北海道・東北', '関東', '中部・北陸', '近畿', '中国・四国', '九州・沖縄'];

    prefSet.forEach((pref) => {
      const area = this.getAreaByPrefecture(pref);
      if (area) {
        if (!areaMap.has(area)) {
          areaMap.set(area, []);
        }
        areaMap.get(area)!.push(pref);
      }
    });

    this.groupedPref = areaOrder
      .filter((area) => areaMap.has(area))
      .map((area) => ({
        label: area,
        value: area,
        items: areaMap
          .get(area)!
          .sort()
          .map((p) => ({
            label: p,
            value: p,
          })),
      }));
  }

  rebuildGroupedStores() {
    // エリア・都道府県フィルタに基づいて店舗選択肢を絞り込む
    let filteredPrefStores = this.allPrefStores;

    // エリアフィルタが設定されている場合
    if (this.selectedArea.length > 0) {
      filteredPrefStores = filteredPrefStores.filter((ps) => {
        const pref = ps.split(this.splitStr)[0];
        const area = this.getAreaByPrefecture(pref);
        return this.selectedArea.includes(area);
      });
    }

    // 都道府県フィルタが設定されている場合
    if (this.selectedPref.length > 0) {
      filteredPrefStores = filteredPrefStores.filter((ps) => {
        const pref = ps.split(this.splitStr)[0];
        return this.selectedPref.includes(pref);
      });
    }

    // 選択肢を構築
    const prefs: string[] = [];
    filteredPrefStores.forEach((ps) => {
      const pref = ps.split(this.splitStr)[0];
      if (!prefs.includes(pref)) {
        prefs.push(pref);
      }
    });

    this.groupedStores = prefs.sort().map((pref) => {
      const stores = filteredPrefStores
        .filter((ps) => ps.startsWith(pref + this.splitStr))
        .map((ps) => ps.split(this.splitStr)[1]);
      return {
        label: pref,
        value: pref,
        items: stores.sort().map((s) => ({
          label: pref + ' ' + s,
          value: s,
        })),
      };
    });

    // フィルタで除外された店舗を選択から除去
    if (this.selectedStores.length > 0) {
      const availableStores = filteredPrefStores.map((ps) => ps.split(this.splitStr)[1]);
      this.selectedStores = this.selectedStores.filter((s) => availableStores.includes(s));
      localStorage.setItem(this.selectedStoreKey, JSON.stringify(this.selectedStores));
    }
  }

  canRegister(): boolean {
    return !!this.searchText && !this.isLoading && this.canRegisterNewItems;
  }

  getStatus(st: boolean): string {
    return st ? '◯' : '×';
  }

  getRowStyle(product: AeonProduct) {
    if (!product.hasAnyStock) {
      return 'unpublic';
    }
    const isZetoStock = product.filteredStocks.filter((s) => s.hasStock).length === 0;
    if (isZetoStock) {
      return 'unpublic';
    }
    return '';
  }

  async doRegister() {
    if (!this.canRegister()) {
      // なぜかdisabledでも押せることがある
      return;
    }
    const searchText = this.searchText;
    if (!searchText) {
      this.message.setFlashMessageWithClear('warn', '入力してください', '');
      return;
    }
    // trim each line, filter empty lines, and remove duplicates
    const texts = [
      ...new Set(
        searchText
          .split(/\n/)
          .map((t) => t.trim())
          .filter((t) => t),
      ),
    ];

    const ngTexts = [];
    this.isLoading = true;
    for (const txt of texts) {
      const splitted = txt.split(/\//, -1);
      let jan: string;
      if (splitted.length > 1) {
        jan = splitted[splitted.length - 1].trim();
        if (!jan) {
          jan = splitted[splitted.length - 2].trim();
        }
      } else {
        jan = txt.trim();
      }
      if (!isValidJan(jan)) {
        ngTexts.push(txt);
      } else {
        await firstValueFrom(this.createGql.mutate({ variables: { jan: jan } }));
      }
    }

    await this.loadProducts();
    if (ngTexts.length > 0) {
      this.message.setFlashMessageWithClear('warn', '登録できなかった商品があります', ngTexts.join('\n'));
    } else {
      this.message.setFlashMessageWithClear('success', '登録しました', '');
    }
    this.searchText = '';
    this.isLoading = false;
  }

  doFilters(stocks: AeonStock[]) {
    stocks = this.doFilterStocks(stocks);
    stocks = this.doFilterArea(stocks);
    stocks = this.doFilterPref(stocks);
    stocks = this.doFilterStores(stocks);
    return stocks;
  }

  private doFilterStocks(stocks: AeonStock[]) {
    if (stocks.length === 0) {
      return stocks;
    }
    // filter by hasStock
    return stocks.filter((s) => s.hasStock);
  }

  private doFilterArea(stocks: AeonStock[]) {
    if (this.selectedArea.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter by area
    return stocks.filter((s) => {
      const area = this.getAreaByPrefecture(s.prefecture);
      return this.selectedArea.includes(area);
    });
  }

  private doFilterPref(stocks: AeonStock[]) {
    if (this.selectedPref.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter by prefecture
    return stocks.filter((s) => {
      return this.selectedPref.includes(s.prefecture);
    });
  }

  private doFilterStores(stocks: AeonStock[]) {
    if (this.selectedStores.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter by store names
    return stocks.filter((s) => {
      return this.selectedStores.includes(s.storeName);
    });
  }

  doChangeArea($e) {
    localStorage.setItem(this.selectedAreaKey, JSON.stringify(this.selectedArea));
    this.rebuildGroupedStores();
    this.products.forEach((p) => {
      p.filteredStocks = this.doFilters(p.all);
    });
    this.logging();
  }

  doChangePref($e) {
    localStorage.setItem(this.selectedPrefKey, JSON.stringify(this.selectedPref));
    this.rebuildGroupedStores();
    this.products.forEach((p) => {
      p.filteredStocks = this.doFilters(p.all);
    });
    this.logging();
  }

  doChangeStores($e) {
    localStorage.setItem(this.selectedStoreKey, JSON.stringify(this.selectedStores));
    this.products.forEach((p) => {
      p.filteredStocks = this.doFilters(p.all);
    });
    this.logging();
  }

  clearArea() {
    this.selectedArea = [];
    this.doChangeArea(null);
  }

  clearPref() {
    this.selectedPref = [];
    this.doChangePref(null);
  }

  clearStores() {
    this.selectedStores = [];
    this.doChangeStores(null);
  }

  onPresetLoaded(preset: StorePreset) {
    this.selectedArea = preset.selectedArea || [];
    this.selectedPref = preset.selectedPref || [];
    this.selectedStores = preset.selectedStores || [];
    this.doChangeArea(null);
    this.doChangePref(null);
    this.doChangeStores(null);
  }

  doSearch() {
    this.loadProducts();
  }

  downloadCsv(table: any) {
    const products: AeonProduct[] = table.filteredValue ?? this.filteredProducts;
    const values = products.filter((p) => p.filteredStocks.length > 0);
    const csv = values
      .map((p) => {
        const stores = p.filteredStocks.map((s) => s.storeName + ':' + s.stockValue).join('/');
        const date = moment(p.createdAt).format('MMDD');
        const totalStock = p.totalStockNum != null ? p.totalStockNum : '-';
        const boughtPastMonth = p.boughtPastMonth != null ? p.boughtPastMonth : '-';
        const memo = `${date}_総在${totalStock}_先販${boughtPastMonth}_${stores}`;
        return p.jan + ',' + p.price + ',1,,"' + (memo ?? '') + '"';
      })
      .join('\n');
    this.outputCsv(csv, 'aeon.csv');
  }

  openAnotherPrice(aeonProduct: AeonProduct) {
    this.anotherPriceModel.aeonProduct = aeonProduct;
    this.showAnotherPriceModal = true;
  }

  async doRegisterAnotherPrice() {
    const id = this.anotherPriceModel.aeonProduct.userAeonId;
    const anotherPrice = this.anotherPriceModel.price;
    const result = await firstValueFrom(this.userAeonUpdateGql.mutate({ variables: { id, anotherPrice } }));

    if (result.data.updateUserAeonProduct) {
      this.anotherPriceModel.aeonProduct.anotherPrice = anotherPrice;
    }

    this.showAnotherPriceModal = false;
    this.anotherPriceModel = {
      aeonProduct: null,
      price: null,
    };
  }

  async doDelete(product: AeonProduct) {
    const result = await firstValueFrom(this.deleteAeonProductGQL.mutate({ variables: { id: product.userAeonId } }));
    if (result.data?.deleteAeonProduct) {
      this.message.setFlashMessageWithClear('success', '削除成功', '削除しました', 3000);
      await this.loadProducts();
    } else {
      this.message.setFlashMessageWithClear(
        'error',
        'エラー',
        '予期せぬエラーが発生しました。もう一度やり直してください。',
        3000,
      );
    }
  }

  private outputCsv(csv: string, filename: string) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const elem = this.elementRef.nativeElement;
    const link = elem.querySelector('#csv-donwload') as HTMLAnchorElement;
    link.href = url;
    link.download = filename;
    link.click();
  }

  private async logging() {
    this.userService.me;
    await firstValueFrom(
      this.loggingGql.fetch({
        variables: {
          name: 'aeon',
          log: JSON.stringify([this.userService.getName(), this.selectedArea, this.selectedPref, this.selectedStores]),
        },
      }),
    );
  }
}
